import React, { useState, useCallback, useRef } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Stack,
  Alert,
  Divider,
  FormControl,
  FormLabel,
  FormHelperText,
  Input,
  ToggleButtonGroup,
} from "@mui/joy";
import {
  FloppyDisk as Save,
  Eye,
  PaperPlaneTilt as Send,
  XCircle as AlertCircle,
  TextT as Type,
  PencilSimple as Edit3,
  Calendar,
  CheckSquare,
  PenNib as PenTool,
  CaretDown as ChevronDown,
  ArrowLeft,
} from "@phosphor-icons/react";
import PDFUpload from "./PDFUpload";
import { PdfFieldDesigner } from "../PdfFieldDesigner";

import { PdfViewerModal } from "./PdfViewerModal";
import { PdfPageManager, type PdfPageChange } from "./PdfPageManager";
import type { Contract, ContractField, PdfPageInfo } from "../../../types";
import { api } from "../../../lib/api";
import { authService } from "../../../lib/auth";

interface ContractDesignerProps {
  contract?: Contract;
  contractId?: number;
  isTemplate?: boolean;
  onSave?: (contract: Contract) => void;
  onPreview?: (contract: Contract) => void;
  onSend?: (contract: Contract) => void;
  onCancel?: () => void;
  readOnly?: boolean;
}

export const ContractDesigner: React.FC<ContractDesignerProps> = ({
  contract,
  contractId,
  isTemplate = false,
  onSave,
  onPreview,
  onSend,
  onCancel,
  readOnly: requestedReadOnly = false,
}) => {
  const [currentContract, setCurrentContract] = useState<Contract | null>(
    contract || null,
  );
  const readOnly =
    requestedReadOnly || (!isTemplate && currentContract?.status !== "draft");
  const fieldBusy = useRef(false);
  const fieldQueueRef = useRef<Promise<void>>(Promise.resolve());
  const pendingFieldOpsRef = useRef(0);
  const latestFieldsRef = useRef<ContractField[]>(
    contract?.contract_fields || [],
  );
  const resolvedFieldIdsRef = useRef<Map<string, string>>(new Map());
  const [fieldSaving, setFieldSaving] = useState(false);
  const [currentTemplate, setCurrentTemplate] = useState<any>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [scale, setScale] = useState(1.0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedFieldId, setSelectedFieldId] = useState<string | null>(null);
  const [selectedFieldType, setSelectedFieldType] =
    useState<string>("signature");
  const [dropdownOptionsText, setDropdownOptionsText] = useState<string>("");
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [showPageManager, setShowPageManager] = useState(false);
  const [loading, setLoading] = useState(false);

  const pdfContainerRef = useRef<HTMLElement>(null);

  React.useEffect(() => {
    latestFieldsRef.current = currentContract?.contract_fields || [];
  }, [currentContract?.contract_fields]);

  // Load template data if contractId is provided and isTemplate is true
  React.useEffect(() => {
    if (contractId && isTemplate && !contract) {
      loadTemplate();
    }
  }, [contractId, isTemplate]);

  const loadTemplate = async () => {
    if (!contractId) return;

    setLoading(true);
    try {
      const accessToken = authService.getAccessToken();
      const response = await fetch(
        `/api/crm/contract-templates/${contractId}/`,
        {
          headers: {
            ...(accessToken && { Authorization: `Bearer ${accessToken}` }),
          },
        },
      );
      if (response.ok) {
        const data = await response.json();
        setCurrentTemplate(data);
        // Convert template data to contract-like structure for the designer
        setCurrentContract({
          id: data.id,
          contract_number: `TEMPLATE-${data.id}`,
          title: data.name,
          pdf_document: data.pdf_url,
          pdf_pages: data.pdf_pages || [],
          contract_fields: data.template_fields || [],
          status: "draft",
          total_value: 0,
          created_at: data.created_at,
          updated_at: data.updated_at,
        } as any);
      }
    } catch (error) {
      setError("Failed to load template");
    } finally {
      setLoading(false);
    }
  };

  const handlePDFUpload = useCallback(
    (updatedContract: Contract, pages: PdfPageInfo[]) => {
      setCurrentContract({
        ...updatedContract,
        pdf_pages: pages,
      });
      setError(null);
    },
    [],
  );

  const handlePDFUploadError = useCallback((error: string) => {
    setError(error);
  }, []);

  const handlePagesChanged = useCallback(
    ({ pageInfo, fields }: PdfPageChange) => {
      // The document is rewritten at the same proxy path, so the URL has to change
      // for the viewer to re-fetch it. The fields come back renumbered - adopting
      // the server's copy is what keeps a field placed on page 4 off page 3 after a
      // delete.
      setCurrentContract((prev) => {
        if (!prev) return prev;
        const basePath = isTemplate
          ? `/api/crm/contract-templates/${prev.id}/view_pdf/`
          : `/api/crm/contracts/${prev.id}/view_pdf/`;
        return {
          ...prev,
          pdf_url: `${basePath}?v=${Date.now()}`,
          pdf_pages: pageInfo,
          contract_fields: fields,
        };
      });
      setSelectedFieldId(null);
      setError(null);
    },
    [isTemplate],
  );

  const handleSave = useCallback(async () => {
    if (!currentContract || readOnly || fieldBusy.current) return;

    setSaving(true);
    try {
      if (isTemplate) {
        // Save template fields
        const accessToken = authService.getAccessToken();
        const response = await fetch(
          `/api/crm/contract-templates/${contractId}/`,
          {
            method: "PATCH",
            headers: {
              "Content-Type": "application/json",
              ...(accessToken && { Authorization: `Bearer ${accessToken}` }),
            },
            body: JSON.stringify({
              template_fields: currentContract.contract_fields,
              pdf_pages: currentContract.pdf_pages,
            }),
          },
        );

        if (!response.ok) {
          throw new Error("Failed to save template");
        }
      } else if (onSave) {
        await onSave(currentContract);
      }

      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save");
    } finally {
      setSaving(false);
    }
  }, [currentContract, onSave, isTemplate, contractId]);

  const handlePreview = useCallback(() => {
    if (onPreview && currentContract) {
      onPreview(currentContract);
    } else if (currentContract?.pdf_url) {
      setShowPreviewModal(true);
    }
  }, [currentContract, onPreview]);

  const mutateFields = async (
    operation: (fields: ContractField[]) => Promise<ContractField[]>,
  ) => {
    if (!currentContract || readOnly) return;
    pendingFieldOpsRef.current += 1;
    fieldBusy.current = true;
    setFieldSaving(true);
    setError(null);

    const nextTask = fieldQueueRef.current.then(async () => {
      const previous = latestFieldsRef.current;
      try {
        const fields = await operation(previous);
        latestFieldsRef.current = fields;
        setCurrentContract((prev) =>
          prev ? { ...prev, contract_fields: fields } : prev,
        );
      } catch (e: any) {
        setError(
          e?.responseData?.error ||
            "Field save failed. The last saved layout has been restored.",
        );
        latestFieldsRef.current = [...previous];
        setCurrentContract((prev) =>
          prev ? { ...prev, contract_fields: [...previous] } : prev,
        );
      } finally {
        pendingFieldOpsRef.current = Math.max(
          0,
          pendingFieldOpsRef.current - 1,
        );
        if (pendingFieldOpsRef.current === 0) {
          fieldBusy.current = false;
          setFieldSaving(false);
        }
      }
    });
    fieldQueueRef.current = nextTask.catch(() => {});
    return nextTask;
  };
  const handleFieldAdd = async (field: ContractField) =>
    mutateFields(async (fields) => {
      const saved = isTemplate
        ? field
        : (
            await api.post<{ field: ContractField }>(
              `/api/crm/contracts/${currentContract!.id}/add_field/`,
              field,
            )
          ).data.field;
      if (saved?.id && saved.id !== field.id) {
        resolvedFieldIdsRef.current.set(field.id, saved.id);
      }
      return [...fields, saved];
    });
  const handleFieldUpdate = async (
    fieldId: string,
    updates: Partial<ContractField>,
  ) =>
    mutateFields(async (fields) => {
      const targetId = resolvedFieldIdsRef.current.get(fieldId) || fieldId;
      const payload = { ...updates, id: targetId };
      if (!isTemplate)
        await api.patch(
          `/api/crm/contracts/${currentContract!.id}/update_field/`,
          { field_id: targetId, ...payload },
        );
      return fields.map((field) =>
        field.id === targetId ? { ...field, ...payload } : field,
      );
    });
  const handleFieldDelete = async (fieldId: string) =>
    mutateFields(async (fields) => {
      const targetId = resolvedFieldIdsRef.current.get(fieldId) || fieldId;
      if (!isTemplate)
        await api.delete(
          `/api/crm/contracts/${currentContract!.id}/remove_field/?field_id=${encodeURIComponent(targetId)}`,
        );
      return fields.filter((field) => field.id !== targetId);
    });

  const resolveFieldId = useCallback(
    (fieldId: string) => resolvedFieldIdsRef.current.get(fieldId),
    [],
  );

  const handleFieldSelect = useCallback((fieldId: string) => {
    setSelectedFieldId(fieldId);
  }, []);

  const selectedField =
    selectedFieldId && currentContract
      ? currentContract.contract_fields.find((f) => f.id === selectedFieldId) ||
        null
      : null;

  const canSend =
    currentContract?.pdf_url && currentContract?.contract_fields.length > 0;
  // Pages are rewritten in place, so the backend only allows it while the
  // contract is still a draft - recipients have already been shown the pages of
  // anything further along.
  const canEditPages = Boolean(
    currentContract?.pdf_url &&
      (isTemplate || currentContract?.status === "draft"),
  );
  const hasChanges = currentContract
    ? JSON.stringify(currentContract) !== JSON.stringify(contract)
    : false;

  return (
    <Box
      sx={{
        width: "100%",
        height: "100vh",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Header */}
      <Card variant="outlined">
        <CardContent>
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            justifyContent="space-between"
            alignItems="center"
          >
            <Stack direction="row" spacing={2} alignItems="center">
              {onCancel && (
                <Button
                  variant="plain"
                  color="neutral"
                  startDecorator={<ArrowLeft size={20} />}
                  onClick={onCancel}
                  sx={{ minWidth: "auto" }}
                >
                  Back
                </Button>
              )}
              <Divider orientation="vertical" />
              <Box>
                <Typography level="title-lg">
                  {currentContract?.title}
                </Typography>
                <Typography level="body-sm" textColor="text.secondary">
                  {currentContract?.contract_number} •{" "}
                  {currentContract?.customer_name}
                </Typography>
              </Box>
            </Stack>

            <Stack direction="row" spacing={1}>
              {!readOnly && (
                <>
                  <Button
                    variant="outlined"
                    color="primary"
                    startDecorator={<Save size={16} />}
                    onClick={handleSave}
                    loading={saving}
                    disabled={!hasChanges || fieldSaving}
                  >
                    Save
                  </Button>

                  <Button
                    variant="outlined"
                    color="primary"
                    startDecorator={<Eye size={16} />}
                    onClick={handlePreview}
                    disabled={!currentContract?.pdf_url}
                  >
                    Preview
                  </Button>

                  <Button
                    variant="solid"
                    color="primary"
                    startDecorator={<Send size={16} />}
                    onClick={() => {
                      if (currentContract)
                        window.location.assign(
                          `/crm/contracts/${currentContract.id}`,
                        );
                    }}
                    disabled={!canSend || fieldSaving || isTemplate}
                  >
                    Review contract
                  </Button>
                </>
              )}
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      <Typography role="status" sx={{ px: 2 }}>
        {fieldSaving || saving
          ? "Saving…"
          : error
            ? "Save failed"
            : isTemplate
              ? "Save to retain template changes"
              : "Field changes saved automatically"}
      </Typography>
      {/* Error Display */}
      {error && (
        <Alert color="danger" sx={{ mx: 2, mt: 2 }}>
          <AlertCircle size={16} />
          {error}
        </Alert>
      )}

      {/* Main Content */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          p: 2,
          gap: 2,
          overflow: "auto",
        }}
      >
        {/* Sidebar - Field Type Selector */}
        {currentContract?.pdf_url && !readOnly && (
          <Box
            sx={{
              width: { xs: "100%", md: 250 },
              flexShrink: 0,
              display: "flex",
              flexDirection: "column",
              gap: 2,
            }}
          >
            <Card variant="outlined">
              <CardContent>
                <Typography level="title-md" sx={{ mb: 2 }}>
                  Field Tools
                </Typography>
                <Stack spacing={2}>
                  <Typography level="body-sm" sx={{ fontWeight: "bold" }}>
                    Select a field type:
                  </Typography>
                  <ToggleButtonGroup
                    value={selectedFieldType}
                    onChange={(event, newValue) => {
                      if (newValue) setSelectedFieldType(newValue);
                    }}
                    orientation="vertical"
                    sx={{ width: "100%" }}
                  >
                    <Button
                      value="signature"
                      variant={
                        selectedFieldType === "signature" ? "solid" : "outlined"
                      }
                      startDecorator={<PenTool size={16} />}
                      sx={{ justifyContent: "flex-start" }}
                    >
                      Signature
                    </Button>
                    <Button
                      value="text"
                      variant={
                        selectedFieldType === "text" ? "solid" : "outlined"
                      }
                      startDecorator={<Type size={16} />}
                      sx={{ justifyContent: "flex-start" }}
                    >
                      Text
                    </Button>
                    <Button
                      value="initial"
                      variant={
                        selectedFieldType === "initial" ? "solid" : "outlined"
                      }
                      startDecorator={<Edit3 size={16} />}
                      sx={{ justifyContent: "flex-start" }}
                    >
                      Initial
                    </Button>
                    <Button
                      value="date"
                      variant={
                        selectedFieldType === "date" ? "solid" : "outlined"
                      }
                      startDecorator={<Calendar size={16} />}
                      sx={{ justifyContent: "flex-start" }}
                    >
                      Date
                    </Button>
                    <Button
                      value="checkbox"
                      variant={
                        selectedFieldType === "checkbox" ? "solid" : "outlined"
                      }
                      startDecorator={<CheckSquare size={16} />}
                      sx={{ justifyContent: "flex-start" }}
                    >
                      Checkbox
                    </Button>
                    <Button
                      value="dropdown"
                      variant={
                        selectedFieldType === "dropdown" ? "solid" : "outlined"
                      }
                      startDecorator={<ChevronDown size={16} />}
                      sx={{ justifyContent: "flex-start" }}
                    >
                      Dropdown
                    </Button>
                  </ToggleButtonGroup>
                  {selectedFieldType === "dropdown" && (
                    <FormControl size="sm">
                      <FormLabel>Dropdown options</FormLabel>
                      <Input
                        value={dropdownOptionsText}
                        onChange={(e) => setDropdownOptionsText(e.target.value)}
                        placeholder="Approved, Declined, Deferred"
                      />
                      <FormHelperText>
                        Comma separated. A dropdown with no options cannot be
                        signed.
                      </FormHelperText>
                    </FormControl>
                  )}
                  <Divider />
                  <Typography level="body-xs" sx={{ mt: 2 }}>
                    Click on the PDF to place a field
                  </Typography>
                </Stack>
              </CardContent>
            </Card>
          </Box>
        )}

        {/* PDF Viewer Area */}
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            minWidth: 0,
          }}
        >
          {currentContract?.pdf_url ? (
            <>
              {/* The uploader stays mounted once a PDF is attached. Its Replace
                  control is the only way to swap out the wrong file, and hiding
                  it left deleting the contract as the only way to correct one. */}
              {!readOnly && (
                <Box sx={{ mb: 2 }}>
                  <PDFUpload
                    contract={currentContract}
                    isTemplate={isTemplate}
                    templateId={isTemplate ? currentContract.id : undefined}
                    onUploadSuccess={handlePDFUpload}
                    onUploadError={handlePDFUploadError}
                    onManagePages={
                      canEditPages ? () => setShowPageManager(true) : undefined
                    }
                  />
                </Box>
              )}
              <Box
                sx={{
                  flex: 1,
                  minHeight: 0,
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <PdfFieldDesigner
                  // Lock edits during a full Save: its PATCH sends a contract_fields snapshot that
                  // would overwrite a field added mid-request. fieldSaving stays out, or the inspector
                  // unmounts on every auto-save (APEX-0022).
                  readOnly={readOnly || saving}
                  resolveFieldId={resolveFieldId}
                  pdfUrl={currentContract.pdf_url}
                  contractId={currentContract.id || contractId || 0}
                  existingFields={currentContract.contract_fields || []}
                  selectedFieldType={selectedFieldType}
                  dropdownOptions={dropdownOptionsText
                    .split(",")
                    .map((option) => option.trim())
                    .filter(Boolean)}
                  onFieldAdded={handleFieldAdd}
                  onFieldUpdated={async (field) => {
                    if (field.id) {
                      await handleFieldUpdate(field.id, field);
                    }
                  }}
                  onFieldDeleted={handleFieldDelete}
                />
              </Box>
            </>
          ) : (
            <Card
              variant="outlined"
              sx={{
                flex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <CardContent sx={{ textAlign: "center", maxWidth: 500 }}>
                {!readOnly ? (
                  <PDFUpload
                    contract={currentContract!}
                    isTemplate={isTemplate}
                    templateId={isTemplate ? currentContract?.id : undefined}
                    onUploadSuccess={handlePDFUpload}
                    onUploadError={handlePDFUploadError}
                  />
                ) : (
                  <Stack spacing={2} alignItems="center">
                    <AlertCircle
                      size={48}
                      color="var(--joy-palette-warning-500)"
                    />
                    <Typography level="title-md">No PDF Document</Typography>
                    <Typography level="body-sm" textColor="text.secondary">
                      This contract doesn't have a PDF document uploaded yet.
                    </Typography>
                  </Stack>
                )}
              </CardContent>
            </Card>
          )}
        </Box>
      </Box>

      {/* Status Bar */}
      <Card variant="outlined">
        <CardContent sx={{ py: 1 }}>
          <Stack direction="row" spacing={4} alignItems="center">
            <Typography level="body-xs" textColor="text.secondary">
              Status: {currentContract?.status}
            </Typography>

            {currentContract?.pdf_pages &&
              currentContract.pdf_pages.length > 0 && (
                <Typography level="body-xs" textColor="text.secondary">
                  Pages: {currentContract.pdf_pages.length}
                </Typography>
              )}

            <Typography level="body-xs" textColor="text.secondary">
              Fields: {currentContract?.contract_fields?.length || 0}
            </Typography>

            {hasChanges && (
              <Typography level="body-xs" color="warning">
                • Unsaved changes
              </Typography>
            )}
          </Stack>
        </CardContent>
      </Card>

      {/* Preview Modal - shows the placed fields, which is the whole point of
          previewing a contract that has been designed */}
      {currentContract?.pdf_url && (
        <PdfViewerModal
          open={showPreviewModal}
          onClose={() => setShowPreviewModal(false)}
          pdfUrl={currentContract.pdf_url}
          title={currentContract.title}
          fields={currentContract.contract_fields}
          fieldValues={currentContract.contract_data}
        />
      )}

      {/* Page Manager */}
      {currentContract?.pdf_url && canEditPages && (
        <PdfPageManager
          open={showPageManager}
          onClose={() => setShowPageManager(false)}
          pdfUrl={currentContract.pdf_url}
          documentId={currentContract.id}
          isTemplate={isTemplate}
          title={`Manage Pages — ${currentContract.title}`}
          onPagesChanged={handlePagesChanged}
        />
      )}
    </Box>
  );
};

export default ContractDesigner;
